import { MapDefinition, MapTileType, MapCoordinate } from '../../editor/MapTypes';
import { DEFAULT_MAP_PATTERNS, DefaultMapPattern } from './DefaultMaps';
import { Pathfinder } from '../Pathfinder';
import { Vector2 } from '../../utils/Vector2';
import { Tile, TileGrid } from '../../types/GameTypes';

const GRID_SIZE = 20;
const MIN_SPAWN_DISTANCE = 12; // Minimum distance between spawn and goal

export class DefaultMapGenerator {
  /**
   * Generate all 10 default maps
   */
  static generateAll(): MapDefinition[] {
    return DEFAULT_MAP_PATTERNS.map(pattern => this.generateMap(pattern));
  }

  /**
   * Generate a single map from a pattern
   */
  static generateMap(pattern: DefaultMapPattern): MapDefinition {
    // Create empty 20x20 grid
    const grid: MapTileType[][] = [];
    for (let y = 0; y < GRID_SIZE; y++) {
      grid[y] = [];
      for (let x = 0; x < GRID_SIZE; x++) {
        grid[y][x] = 'empty';
      }
    }

    // Generate pattern
    pattern.generatePattern(grid);

    // Randomly place spawn and goal with validation
    const { spawn, goal } = this.findRandomSpawnAndGoal(grid);

    // Ensure spawn and goal are empty
    grid[spawn.y][spawn.x] = 'empty';
    grid[goal.y][goal.x] = 'empty';

    // Validate path exists
    const pathfinder = new Pathfinder();
    const gameGrid = this.convertToGameGrid(grid);
    const path = pathfinder.findPath(
      new Vector2(spawn.x, spawn.y),
      new Vector2(goal.x, goal.y),
      gameGrid,
      false
    );

    if (path.length === 0) {
      console.warn(`[DefaultMapGenerator] No valid path found for ${pattern.name}, creating direct path`);
      // Create direct path as fallback
      this.createDirectPath(grid, spawn.x, spawn.y, goal.x, goal.y);
    }

    // Create map definition
    const now = Date.now();
    const map: MapDefinition = {
      id: pattern.id,
      name: pattern.name,
      author: 'System',
      difficultyTags: pattern.difficultyTags,
      grid,
      spawns: [{ x: spawn.x, y: spawn.y }],
      goal: { x: goal.x, y: goal.y },
      props: [],
      metadata: {
        description: pattern.description,
        createdAt: now,
        updatedAt: now,
        scripts: [],
      },
    };

    return map;
  }

  /**
   * Find random spawn and goal positions that are far apart and have a valid path
   */
  private static findRandomSpawnAndGoal(grid: MapTileType[][]): { spawn: MapCoordinate; goal: MapCoordinate } {
    const pathfinder = new Pathfinder();
    const gameGrid = this.convertToGameGrid(grid);
    const maxAttempts = 50;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Random spawn position (avoid edges)
      const spawnX = Math.floor(Math.random() * (GRID_SIZE - 4)) + 2;
      const spawnY = Math.floor(Math.random() * (GRID_SIZE - 4)) + 2;
      
      // Random goal position (avoid edges and ensure minimum distance)
      let goalX = Math.floor(Math.random() * (GRID_SIZE - 4)) + 2;
      let goalY = Math.floor(Math.random() * (GRID_SIZE - 4)) + 2;
      
      // Ensure minimum distance
      const distance = Math.abs(spawnX - goalX) + Math.abs(spawnY - goalY);
      if (distance < MIN_SPAWN_DISTANCE) {
        // Try to place goal on opposite side
        if (spawnX < GRID_SIZE / 2) {
          goalX = Math.floor(Math.random() * (GRID_SIZE / 2 - 2)) + GRID_SIZE / 2 + 2;
        } else {
          goalX = Math.floor(Math.random() * (GRID_SIZE / 2 - 2)) + 2;
        }
        if (spawnY < GRID_SIZE / 2) {
          goalY = Math.floor(Math.random() * (GRID_SIZE / 2 - 2)) + GRID_SIZE / 2 + 2;
        } else {
          goalY = Math.floor(Math.random() * (GRID_SIZE / 2 - 2)) + 2;
        }
      }
      
      // Check if positions are valid (not walls)
      if (grid[spawnY][spawnX] === 'wall' || grid[goalY][goalX] === 'wall') {
        continue;
      }
      
      // Check if path exists
      const path = pathfinder.findPath(
        new Vector2(spawnX, spawnY),
        new Vector2(goalX, goalY),
        gameGrid,
        false
      );
      
      if (path.length > 0) {
        return { spawn: { x: spawnX, y: spawnY }, goal: { x: goalX, y: goalY } };
      }
    }
    
    // Fallback: use opposite corners if random placement fails
    console.warn(`[DefaultMapGenerator] Failed to find random spawn/goal after ${maxAttempts} attempts, using fallback`);
    return { spawn: { x: 2, y: 2 }, goal: { x: 17, y: 17 } };
  }

  /**
   * Convert MapTileType grid to game TileGrid for pathfinding
   */
  private static convertToGameGrid(grid: MapTileType[][]): TileGrid {
    const gameGrid: Tile[][] = [];
    for (let y = 0; y < GRID_SIZE; y++) {
      gameGrid[y] = [];
      for (let x = 0; x < GRID_SIZE; x++) {
        gameGrid[y][x] = {
          x,
          y,
          type: grid[y][x] === 'wall' ? 'wall' : 'empty',
        };
      }
    }
    return gameGrid;
  }

  /**
   * Create a direct path from start to goal (fallback)
   */
  private static createDirectPath(
    grid: MapTileType[][],
    startX: number,
    startY: number,
    goalX: number,
    goalY: number
  ): void {
    // Clear horizontal path
    const minX = Math.min(startX, goalX);
    const maxX = Math.max(startX, goalX);
    for (let x = minX; x <= maxX; x++) {
      grid[startY][x] = 'empty';
    }

    // Clear vertical path
    const minY = Math.min(startY, goalY);
    const maxY = Math.max(startY, goalY);
    for (let y = minY; y <= maxY; y++) {
      grid[y][goalX] = 'empty';
    }
  }

  /**
   * Check if a map ID is a default map
   */
  static isDefaultMap(id: string): boolean {
    return id.startsWith('default_');
  }
}

